import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
  Box,
  Card,
  Stack,
  Typography,
  IconButton,
  Button,
  Chip,
  Tooltip,
  Popover,
  LinearProgress,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { MunicipioSelectV2 } from '../../../components/v2';
import { tableV2LoadingLinearSx } from '../../../components/v2/tableV2Styles';
import * as actionsCalendarioFeriado from '../../../store/calendarioFeriado/actions';
import * as selectorsCalendarioFeriado from '../../../store/calendarioFeriado/reducer';
import CalendarGrid from './CalendarGrid';
import {
  buildMonthDays,
  buildWeekDays,
  buildGroupedLocalidadesDisplay,
  buildProximosFeriadoGroupKey,
  formatGroupedFeriadoTooltip,
  getGroupedLocalidades,
  groupFeriadosByDate,
  groupProximosFeriados,
  TIPO_COLOR,
  TIPO_LABEL,
  tipoChipSx,
} from './dateUtils';

moment.locale('pt-br');

const ProximoFeriadoRow = ({ group, index, showLocalidade }) => {
  const [localidadesAnchor, setLocalidadesAnchor] = useState(null);
  const locDisplay = showLocalidade
    ? buildGroupedLocalidadesDisplay(getGroupedLocalidades(group.items))
    : null;
  const overflowLabel = locDisplay?.overflowCount === 1 ? 'município' : 'municípios';

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={1}
      sx={{
        px: 2,
        py: 1.25,
        borderTop: index === 0 ? 'none' : '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 38,
          height: 38,
          borderRadius: 2,
          bgcolor: 'action.hover',
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.1, fontSize: '0.8rem' }}>
          {moment(group.data).format('DD')}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textTransform: 'uppercase', fontSize: '0.55rem' }}
        >
          {moment(group.data).format('MMM')}
        </Typography>
      </Stack>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Tooltip title={formatGroupedFeriadoTooltip(group, { showLocalidade })} enterDelay={400}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: '0.8rem',
              lineHeight: 1.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {group.nome}
          </Typography>
        </Tooltip>
        {locDisplay && (
          <Tooltip title={locDisplay.tooltipText} enterDelay={400}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25, lineHeight: 1.35 }}
            >
              {locDisplay.overflowCount > 0 ? (
                <>
                  {locDisplay.preview},{' '}
                  <Box
                    component="span"
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      setLocalidadesAnchor(e.currentTarget);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setLocalidadesAnchor(e.currentTarget);
                      }
                    }}
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    e mais {locDisplay.overflowCount} {overflowLabel}
                  </Box>
                </>
              ) : (
                locDisplay.all.join(', ')
              )}
            </Typography>
          </Tooltip>
        )}
      </Box>

      <Chip
        size="small"
        sx={{
          ...tipoChipSx(group.tipo),
          height: 22,
          fontSize: '0.65rem',
          flexShrink: 0,
          '& .MuiChip-label': { px: 0.75 },
        }}
        label={TIPO_LABEL[group.tipo] || group.tipo}
      />

      <Popover
        open={Boolean(localidadesAnchor)}
        anchorEl={localidadesAnchor}
        onClose={() => setLocalidadesAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, maxWidth: 280 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {group.nome}
          </Typography>
          <Stack spacing={0.5}>
            {locDisplay?.all.map((localidade) => (
              <Typography key={localidade} variant="body2" color="text.secondary">
                {localidade}
              </Typography>
            ))}
          </Stack>
        </Box>
      </Popover>
    </Stack>
  );
};

ProximoFeriadoRow.propTypes = {
  group: PropTypes.shape({
    data: PropTypes.string.isRequired,
    nome: PropTypes.string.isRequired,
    tipo: PropTypes.string.isRequired,
    items: PropTypes.array.isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  showLocalidade: PropTypes.bool,
};

ProximoFeriadoRow.defaultProps = {
  showLocalidade: false,
};

// TODO: alternância Mês/Semana escondida por ora (não fazia sentido nesta primeira versão da
// tela) — a lógica de semana (buildWeekDays, periodoLabel, proximosFeriados) continua pronta
// para quando o toggle voltar a ser exposto na UI.
const viewMode = 'month';

const Calendario = ({ feriados, loadingCalendario, loadMunicipioUsuario, loadCalendarioPublico }) => {
  const [municipiosIbge, setMunicipiosIbge] = useState([]);
  const [municipioUsuario, setMunicipioUsuario] = useState(null);
  const [referenceDate, setReferenceDate] = useState(moment());
  const [initialResolved, setInitialResolved] = useState(false);

  useEffect(() => {
    loadMunicipioUsuario((data) => {
      setMunicipioUsuario(data || null);
      if (data && data.codigoIbge) {
        setMunicipiosIbge([String(data.codigoIbge)]);
      }
      setInitialResolved(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ano = referenceDate.year();
  const municipiosKey = municipiosIbge.join(',');

  useEffect(() => {
    if (!initialResolved) return;
    if (!municipiosIbge.length) return;
    loadCalendarioPublico(municipiosIbge, ano);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialResolved, municipiosKey, ano, loadCalendarioPublico]);

  const feriadosByDate = useMemo(() => groupFeriadosByDate(feriados), [feriados]);

  const days = useMemo(
    () => (viewMode === 'month' ? buildMonthDays(referenceDate) : buildWeekDays(referenceDate)),
    [viewMode, referenceDate]
  );

  const periodoLabel = useMemo(() => {
    if (viewMode === 'month') return referenceDate.format('MMMM YYYY');
    const start = moment(referenceDate).startOf('week');
    const end = moment(referenceDate).endOf('week');
    return `${start.format('D MMM')} – ${end.format('D MMM')}`;
  }, [viewMode, referenceDate]);

  const navigate = useCallback(
    (delta) => {
      setReferenceDate((prev) =>
        moment(prev).add(delta, viewMode === 'month' ? 'month' : 'week'));
    },
    [viewMode]
  );

  const goToToday = useCallback(() => setReferenceDate(moment()), []);

  const showLocalidade = municipiosIbge.length > 1;

  const proximosFeriados = useMemo(() => {
    const inicioPeriodo = viewMode === 'month'
      ? moment(referenceDate).startOf('month')
      : moment(referenceDate).startOf('week');
    const filtrados = (feriados || [])
      .filter((f) => f.status === 'VALIDO' && moment(f.data).isSameOrAfter(inicioPeriodo, 'day'))
      .sort((a, b) => moment(a.data).valueOf() - moment(b.data).valueOf());

    const lista = showLocalidade
      ? groupProximosFeriados(filtrados)
      : filtrados.map((f) => ({ data: f.data, nome: f.nome, tipo: f.tipo, items: [f] }));

    return lista.slice(0, 10);
  }, [feriados, referenceDate, viewMode, showLocalidade]);

  const loadingContentSx = {
    opacity: loadingCalendario ? 0.38 : 1,
    transition: (theme) =>
      theme.transitions.create('opacity', {
        duration: theme.transitions.duration.shorter,
      }),
    pointerEvents: loadingCalendario ? 'none' : 'auto',
  };

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Typography variant="h4">Calendário de Feriados</Typography>
          <Box sx={{ width: { xs: '100%', md: 420 } }}>
            <MunicipioSelectV2
              label="Municípios"
              multiSelect
              value={municipiosIbge}
              nome={municipioUsuario?.municipioNome}
              uf={municipioUsuario?.uf}
              defaultCodigoIbge={municipioUsuario?.codigoIbge}
              onSelect={(ids) => setMunicipiosIbge(ids || [])}
            />
          </Box>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1.55fr 0.68fr' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          <Card
            variant="outlined"
            sx={{
              minWidth: 0,
              width: '100%',
              borderRadius: 4,
              p: { xs: 2, sm: 2.5 },
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {loadingCalendario && <LinearProgress sx={tableV2LoadingLinearSx} />}
            <Box sx={loadingContentSx}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="center"
              alignItems="center"
              spacing={2}
              sx={{ mb: 2, position: 'relative' }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton onClick={() => navigate(-1)} size="small">
                  <ChevronLeftIcon />
                </IconButton>
                <Typography variant="h6" sx={{ minWidth: 160, textAlign: 'center', textTransform: 'capitalize' }}>
                  {periodoLabel}
                </Typography>
                <IconButton onClick={() => navigate(1)} size="small">
                  <ChevronRightIcon />
                </IconButton>
              </Stack>

              <Button
                size="small"
                variant="outlined"
                onClick={goToToday}
                sx={{ position: { xs: 'static', sm: 'absolute' }, right: 0 }}
              >
                Hoje
              </Button>
            </Stack>

            {!municipiosIbge.length && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Selecione um ou mais municípios acima para ver os feriados no calendário.
              </Typography>
            )}
            <CalendarGrid
              days={days}
              referenceDate={referenceDate}
              feriadosByDate={feriadosByDate}
              viewMode={viewMode}
              showLocalidade={showLocalidade}
            />

            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              {Object.keys(TIPO_LABEL).map((tipo) => (
                <Stack key={tipo} direction="row" alignItems="center" spacing={0.5}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: TIPO_COLOR[tipo].main,
                    }}
                  />
                  <Typography variant="caption">{TIPO_LABEL[tipo]}</Typography>
                </Stack>
              ))}
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    border: '1px dashed',
                    borderColor: 'error.main',
                  }}
                />
                <Typography variant="caption">Cancelado</Typography>
              </Stack>
            </Stack>
            </Box>
          </Card>

          <Card
            variant="outlined"
            sx={{ minWidth: 0, width: '100%', borderRadius: 4, p: 0, overflow: 'hidden' }}
          >
            <Box sx={loadingContentSx}>
            <Typography variant="subtitle1" sx={{ px: 2, pt: 2, pb: 1, fontWeight: 700 }}>
              Próximos feriados
            </Typography>
            {proximosFeriados.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
                Nenhum feriado futuro encontrado.
              </Typography>
            ) : (
              <Stack>
                {proximosFeriados.map((group, index) => (
                  <ProximoFeriadoRow
                    key={buildProximosFeriadoGroupKey(group.items[0])}
                    group={group}
                    index={index}
                    showLocalidade={showLocalidade}
                  />
                ))}
              </Stack>
            )}
            </Box>
          </Card>
        </Box>
      </Stack>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  feriados: selectorsCalendarioFeriado.getFeriados(state),
  loadingCalendario: selectorsCalendarioFeriado.getLoadingCalendario(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...actionsCalendarioFeriado,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Calendario));
